import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import Clickable from 'components/Common/Clickable'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import withMountGuard from 'hocs/withMountGuard'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import { nonNullable } from 'lib/array/arrayUtils'

const Container = styled(Clickable)`
  padding: ${rem(12)} ${rem(16)};
  color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
  border-radius: ${props => props.theme.borderRadius.M};
  /** To see the effect of the border-radius */
  overflow: hidden;
`

const CustomDivider = styled(Divider)`
  && {
    background-color: ${props => props.theme.palette.product.loyalty.gold.darkContrast};
  }
`

interface Props {
  onLinkClick: () => void
  config: App.LoyaltyConfig
}

function AccountMenuLoyaltyNoAccountGoldTile({ onLinkClick, config }: Props) {
  const dispatch = useAppDispatch()
  const loyaltyUpgradesPromo = useLabFeatureFlag(LabFeatureFlags.enableLoyaltyUpgradesPromo)

  const handleActivateMembership = useCallback(() => {
    dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
    onLinkClick()
  }, [dispatch, onLinkClick])

  const { regionTierBenefits } = config

  const benefits = useMemo(() => {
    const orderedBenefits: Array<App.LoyaltyBenefitType> = [
      'hotel_upgrade',
      'airport_transfer',
      'lounge_pass',
      'discounted_insurance',
      'phone_support'
    ]

    return nonNullable(orderedBenefits.map(benefitType => {
      const benefit = regionTierBenefits.gold.find(benefit => benefit.code === benefitType)
      if (benefit?.code === 'phone_support') {
        const loungePassBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'lounge_pass')
        // We don't need to show phone support if we have a lounge pass available
        if (loungePassBenefit) return
      }
      return benefit
    }))
  }, [regionTierBenefits])

  return <Container to={`/${LOYALTY_LANDING_PAGE}`} onClick={onLinkClick}>
    <VerticalSpacer gap={20}>
      <VerticalSpacer gap={12}>
        <Group direction="horizontal" horizontalAlign="space-between">
          <LoyaltyProgramNameLogo width={72} />
          <BodyText variant="medium" format="uppercase">Gold</BodyText>
        </Group>
        <LoyaltyTierLabel
          tier="gold"
          kind="primary"
          format="uppercase"
        >
          EXCLUSIVE INVITE
        </LoyaltyTierLabel>
        <Heading variant="heading4">
          You've unlocked{' '}
          <Colour value="tier-gold-light-contrast">Gold status</Colour>
        </Heading>
      </VerticalSpacer>
      <VerticalSpacer gap={8}>
        <BodyText variant="small" weight="bold">
          Your benefits, ready when you are
        </BodyText>
        {benefits.map((benefit, idx) => (
          <React.Fragment key={benefit.code}>
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
              {benefit.code === 'hotel_upgrade' && <>
                <BodyTextBlock variant="small" startIcon={<LineCrownIcon />}>
                  Free room or package upgrade
                </BodyTextBlock>
                <Group direction="horizontal" verticalAlign="center" gap={12}>
                  {loyaltyUpgradesPromo?.enabled && <PromotionLabel variant="premium">New</PromotionLabel>}
                  <LoyaltyTierLabel kind="tertiary" tier="gold">
                    {benefit.unlimited ? 'Unlimited' : benefit.limit}x
                  </LoyaltyTierLabel>
                </Group>
              </>}
              {benefit.code === 'airport_transfer' && <>
                <BodyTextBlock variant="small" startIcon={<LineTaxiIcon />}>
                  Free airport chauffeur service
                </BodyTextBlock>
                <LoyaltyTierLabel kind="tertiary" tier="gold">
                  {benefit.unlimited ? 'Unlimited' : benefit.limit}x
                </LoyaltyTierLabel>
              </>}
              {benefit.code === 'lounge_pass' && <>
                <BodyTextBlock variant="small" startIcon={<LineChairWithAirplaneIcon />}>
                  Free airport lounge passes
                </BodyTextBlock>
                <LoyaltyTierLabel kind="tertiary" tier="gold">
                  {benefit.unlimited ? 'Unlimited' : benefit.limit}x
                </LoyaltyTierLabel>
              </>}
              {benefit.code === 'discounted_insurance' && <>
                <BodyTextBlock variant="small" startIcon={<LineShieldCheckIcon />}>
                  Discounted travel insurance
                </BodyTextBlock>
                <LoyaltyTierLabel kind="tertiary" tier="gold">
                  {benefit.value?.amount}%
                </LoyaltyTierLabel>
              </>}
              {benefit.code === 'phone_support' && <BodyTextBlock variant="small" startIcon={<LinePhoneIcon />}>
                Priority phone support 24/7
              </BodyTextBlock>}
            </Group>
            {idx < benefits.length - 1 && <CustomDivider kind="primary" />}
          </React.Fragment>
        ))}
      </VerticalSpacer>
      <TextButton kind="primary" size="medium" fit="full-width" onClick={handleActivateMembership}>
        Activate free membership
      </TextButton>
    </VerticalSpacer>
  </Container>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLoyaltyEnabled)
})(AccountMenuLoyaltyNoAccountGoldTile)
